import { Controller } from "@hotwired/stimulus"

const number = new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const price = (cents) => `${number.format(cents / 100)} €`;

// Ventilation de l'étape « Ventilation et reversements » (InsurancePolicies::TarificationComponent), au format
// de la grille des politiques de prix : onglets Ventilation / Visualisation, reversements par combinaison
// recalculés en direct sur toute la chaîne, saisie d'un type de bien qui vaut pour tous ses tarifs. Le
// repli des groupes est celui de pricing-grid. « Enregistrer » (name=stay) revient sur le même onglet.
export default class extends Controller {
  static targets = ["tab", "pane", "heading", "tabInput", "status", "net", "groupNet", "reverseValue", "reverseKind", "groupValue", "groupUnit"];
  static values = { tab: String, dirtyLabel: String, mixed: String };

  connect() {
    this.markDirty = () => {
      this.statusTarget.textContent = this.dirtyLabelValue;
      this.statusTarget.classList.add("text-warning");
    };
    this.element.addEventListener("input", this.markDirty);
  }

  disconnect() {
    this.element.removeEventListener("input", this.markDirty);
  }

  select(event) {
    this.tabValue = event.currentTarget.dataset.tab;
    this.tabInputTarget.value = this.tabValue;
    this.tabTargets.forEach((el) => el.setAttribute("aria-selected", el.dataset.tab === this.tabValue));
    [...this.paneTargets, ...this.headingTargets].forEach((el) => (el.hidden = el.dataset.tab !== this.tabValue));
  }

  refreshCascade(event) {
    this.refreshKey(event.currentTarget.dataset.key);
    this.refreshGroupInputs(event.currentTarget.dataset.group, event.currentTarget.dataset.actor);
  }

  toggleUnit(event) {
    const { key, actor } = event.currentTarget.dataset;
    const kind = this.find(this.reverseKindTargets, key, actor);
    kind.value = kind.value === "amount" ? "percentage" : "amount";
    event.currentTarget.textContent = kind.value === "amount" ? "€" : "%";
    this.markDirty();
    this.refreshKey(key);
  }

  // Un type de bien : sa saisie remplit tous ses tarifs, pour cet acteur.
  groupChanged(event) {
    const { group, actor } = event.currentTarget.dataset;
    this.leaves(group, actor).forEach((input) => {
      input.value = event.currentTarget.value;
      this.refreshKey(input.dataset.key);
    });
  }

  toggleGroupUnit(event) {
    const { group, actor } = event.currentTarget.dataset;
    const unit = event.currentTarget.textContent.trim() === "€" ? "percentage" : "amount";
    event.currentTarget.textContent = unit === "amount" ? "€" : "%";
    this.leaves(group, actor).forEach((input) => {
      const { key } = input.dataset;
      this.find(this.reverseKindTargets, key, actor).value = unit;
      input.closest(".input-group").querySelector(".tarif__unit").textContent = event.currentTarget.textContent;
      this.refreshKey(key);
    });
    this.markDirty();
  }

  // Cascade d'une combinaison, comme cascade.ts : chaque acteur détient son gain propre et ce qu'il reçoit,
  // en reverse une part au suivant (Version::Reversement#amount_cents : % de ce qu'il détient, ou euros,
  // borné), garde le reste.
  refreshKey(key) {
    const nets = this.netTargets.filter((el) => el.dataset.key === key).sort((a, b) => a.dataset.actor - b.dataset.actor);
    let received = 0;
    nets.forEach((net) => {
      const { actor } = net.dataset;
      const held = (parseInt(net.dataset.own, 10) || 0) + received;
      const input = this.find(this.reverseValueTargets, key, actor);
      const value = input ? parseFloat(input.value.replace(",", ".")) || 0 : 0;
      const amount = this.find(this.reverseKindTargets, key, actor)?.value === "amount";
      const wanted = amount ? Math.round(value * 100) : Math.round((held * value) / 100);
      const sent = Math.min(Math.max(wanted, 0), Math.max(held, 0));
      net.dataset.net = held - sent;
      net.textContent = price(held - sent);
      net.classList.toggle("text-danger", held - sent < 0);
      received = sent;
      if (net.dataset.group) this.refreshGroupNet(net.dataset.group, actor);
    });
  }

  refreshGroupNet(group, actor) {
    const target = this.groupNetTargets.find((el) => el.dataset.group === group && el.dataset.actor === String(actor));
    if (!target) return;
    const values = this.netTargets.filter((el) => el.dataset.group === group && el.dataset.actor === String(actor)).map((el) => Number(el.dataset.net));
    const [min, max] = [Math.min(...values), Math.max(...values)];
    target.textContent = min === max ? price(min) : `${price(min)} – ${price(max)}`;
  }

  // La saisie d'un groupe montre la valeur commune de ses tarifs, sinon « Variable ».
  refreshGroupInputs(group, actor) {
    if (!group) return;
    const input = this.groupValueTargets.find((el) => el.dataset.group === group && el.dataset.actor === String(actor));
    if (!input) return;
    const values = new Set(this.leaves(group, actor).map((el) => el.value));
    input.value = values.size === 1 ? [...values][0] : "";
    input.placeholder = values.size === 1 ? "" : this.mixedValue;
  }

  leaves(group, actor) {
    return this.reverseValueTargets.filter((el) => el.dataset.group === group && el.dataset.actor === String(actor));
  }

  find(targets, key, actor) {
    return targets.find((el) => el.dataset.key === key && el.dataset.actor === String(actor));
  }
}
